import { useApp } from '@/App';
import { DocumentType } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	useGetPOApprovalList,
	useProcessPOApproval,
	usePrintPurchaseOrder,
} from '@/libs/fleet-api/hooks/purchase-order';
import { ProcessPOApprovalRequest } from '@/libs/fleet-api/services/purchase-order';
import {
	CheckCircleFilled,
	FileSearchOutlined,
	InfoCircleFilled,
	ArrowLeftOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Space,
	Table,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
const pageSize = 20;

const FBDHPOApprovalListPage: FC<{
	mppId: number;
	mppNo: string;
	spfNo: string;
	mklNo: string;
	onBack?: () => void;
}> = ({ mppId, spfNo, mklNo, mppNo, onBack }) => {
	const { navigate } = useApp();
	const [page, setPage] = useState(1);
	const [successModal, setSuccessModal] = useState('');
	const [approvalModal, setApprovalModal] = useState(false);

	const { data, isLoading, error, refetch } = useGetPOApprovalList({
		id: mppId,
	});

	const { form, fieldName } = useForm<ProcessPOApprovalRequest>();

	const selectedPOId = useWatch(
		fieldName(['approvals', 0, 'approvalId']),
		form,
	);

	const approveAction = useWatch(
		fieldName(['approvals', 0, 'approvalAction']),
		form,
	);

	const { mutateAsync } = useProcessPOApproval();
	const printPurchaseOrderMutation = usePrintPurchaseOrder();
	const { openDocumentModal } = useModals();

	const handleCloseSuccessModal = () => setSuccessModal('');

	const clearFormValue = () => {
		form.setFieldValue(fieldName(['approvals', 0, 'approvalId']), undefined);
		form.setFieldValue(
			fieldName(['approvals', 0, 'approvalAction']),
			undefined,
		);
		form.setFieldValue(fieldName(['approvals', 0, 'fbdhNotes']), undefined);
		form.setFieldValue(fieldName(['approvals', 0, 'requestType']), undefined);
	};

	const onFormFinish = async (formValues: ProcessPOApprovalRequest) => {
		try {
			await mutateAsync(formValues);
			setSuccessModal(
				'PO berhasil di-' + formValues.approvals[0].approvalAction + '!',
			);
			setApprovalModal(false);
		} catch (error) {
			console.error(error);
		} finally {
			refetch();
			clearFormValue();
		}
	};

	useEffect(() => {
		if (selectedPOId && data) {
			const record = data?.find((n) => n.id === selectedPOId);
			form.setFieldValue(
				fieldName(['approvals', 0, 'approvalId']),
				selectedPOId,
			);
			form.setFieldValue(
				fieldName(['approvals', 0, 'requestType']),
				record?.requestType,
			);
		} else {
			clearFormValue();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, selectedPOId]);

	return (
		<>
			{/* SUCCESS MODAL */}
			<FleetModal
				open={successModal.length > 0}
				closable={false}
				maskClosable={false}
				width={600}
				onOk={handleCloseSuccessModal}
				okText="OK"
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<CheckCircleFilled
						style={{ fontSize: 48, color: '#48A11E', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					{successModal}
				</div>
			</FleetModal>

			{/* PO APPROVAL MODAL */}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="vertical"
				onFinish={onFormFinish}
				scrollToFirstError
			>
				<FleetModal
					open={approvalModal && !!selectedPOId}
					footer={null}
					closable={false}
					maskClosable={false}
					width={500}
					style={{
						padding: '36px 32px 24px 32px',
						borderRadius: 12,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<InfoCircleFilled
							style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
						/>
					</div>
					<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 32 }}>
						{`Apakah anda ingin melakukan ${approveAction} PO dengan detail berikut?`}
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
						<div>
							<Divider />

							{(() => {
								const record = data?.find((item) => item.id === selectedPOId);

								return (
									<>
										<Row>
											<Col span={8}>No. PO</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.purchaseOrderNo}
											</Col>
										</Row>
										<Row>
											<Col span={8}>Merk Asset</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.brand}
											</Col>
										</Row>
										<Row>
											<Col span={8}>Jumlah Unit</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.totalUnit}
											</Col>
										</Row>
									</>
								);
							})()}
							<Divider />

							<Row>
								<Col span={24}>
									<Form.Item label="Notes FRM">
										<TextArea
											value={data?.[0]?.frmNotes}
											disabled
											readOnly
										/>
									</Form.Item>
								</Col>
							</Row>
							<Divider />
							<Row>
								<Col span={24}>
									<Form.Item
										name={fieldName(['approvals', 0, 'fbdhNotes'])}
										label="Notes"
										required
									>
										<TextArea />
									</Form.Item>
								</Col>
							</Row>
						</div>
						<Divider style={{ opacity: 0, marginBottom: 48 }} />
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								gap: 16,
								width: '100%',
								marginBottom: 24,
							}}
						>
							<Button
								onClick={() => setApprovalModal(false)}
								type="default"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									border: '1px solid #1557BC',
									color: '#1557BC',
									background: 'white',
								}}
							>
								BACK
							</Button>
							<Button
								htmlType="submit"
								type="primary"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									background: '#1557BC',
									color: 'white',
									border: 'none',
								}}
								onClick={form.submit}
							>
								SUBMIT
							</Button>
						</div>
					</div>
				</FleetModal>
				<Form.Item
					style={{ display: 'none' }}
					name={fieldName(['approvals', 0, 'approvalId'])}
				>
					<Input />
				</Form.Item>

				<Form.Item
					name={fieldName(['approvals', 0, 'requestType'])}
					hidden
				>
					<Input />
				</Form.Item>
				<Form.Item
					name={fieldName(['approvals', 0, 'approvalAction'])}
					hidden
				>
					<Input />
				</Form.Item>
			</Form>

			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							if (onBack) {
								onBack();
							} else {
								navigate('FBDHPODetailListPage', {
									mppId,
									mklNo,
									spfNo,
								});
							}
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Approval Purchase Order (PO)</FleetTitle>
				</div>
			</div>

			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<Table
					scroll={{ x: 'max-content' }}
					bordered
					loading={isLoading}
					dataSource={data}
					columns={[
						{
							title: 'No',
							render: (_, __, index) => <span>{index + 1}</span>,
							align: 'center',
							width: 1,
						},
						{ title: 'No. MPP', render: () => <span>{mppNo}</span> },
						{
							title: 'No. PO',
							dataIndex: 'purchaseOrderNo',
							render: (v) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMPODetailListPage', {
											mklNo: mklNo,
											spfNo: spfNo,
											mppId: mppId,
											onBack: () =>
												navigate('FBDHPOApprovalListPage', {
													mppId,
													mppNo,
													mklNo,
													spfNo,
												}),
										});
									}}
								>
									{v}
								</a>
							),
						},
						{ title: 'Supplier', dataIndex: 'supplierName' },
						{ title: 'Merk Asset', dataIndex: 'brand' },
						{
							title: 'Tanggal Terbit PO',
							dataIndex: 'purchaseOrderReleaseDate',
							render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
						},
						{
							title: 'Tanggal PO Expired',
							dataIndex: 'purchaseOrderExpiredDate',
							render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
						},
						// {
						// 	title: 'Tanggal Perpanjangan PO',
						// 	dataIndex: 'purchaseOrderExtendDate',
						// },
						// { title: 'Status PO', dataIndex: 'status' },
						{
							title: 'Jumlah Unit',
							dataIndex: 'totalUnit',
						},
						{
							title: 'Request Type',
							dataIndex: 'requestType',
						},
						{
							title: 'View PO Awal',
							align: 'center',
							width: 100,
							render: (_, record) => {
								return (
									<a
										onClick={async (e) => {
											e.preventDefault();
											if (!record.purchaseOrderDocument) {
												// Call download endpoint when document is null
												if (!record.purchaseOrderId) {
													return;
												}
												try {
													const response =
														await printPurchaseOrderMutation.mutateAsync({
															purchaseOrderIds: [record.purchaseOrderId],
														});
													openDocumentModal(response);
												} catch (error) {
													console.error(
														'Failed to download PO document:',
														error,
													);
												}
												return;
											}
											openDocumentModal([
												{
													...record.purchaseOrderDocument,
													type: record.purchaseOrderDocument
														.format as unknown as DocumentType,
												},
											]);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},

						{
							title: 'Select',
							align: 'center',
							width: 1,
							render: (_, record) => (
								<Checkbox
									// Add this line:
									checked={selectedPOId === record.id}
									onChange={() =>
										form.setFieldValue(
											fieldName(['approvals', 0, 'approvalId']),
											!selectedPOId ? record.id : undefined,
										)
									}
									disabled={!!selectedPOId && selectedPOId !== record.id}
								/>
							),
						},
					]}
					pagination={{
						hideOnSinglePage: true,
						current: page,
						total: 0,
						pageSize,
						onChange: setPage,
					}}
					rowKey="key"
				/>
				{error && (
					<div style={{ color: 'red' }}>
						Gagal memuat detail purchase order.
					</div>
				)}
				<Row
					style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}
				>
					<Row style={{ gap: 23 }}>
						<Button
							style={{ width: 215 }}
							type="primary"
							disabled={!selectedPOId}
							onClick={() => {
								form.setFieldValue(
									fieldName(['approvals', 0, 'approvalAction']),
									'return',
								);
								setApprovalModal(true);
							}}
						>
							RETURN PO
						</Button>
						<Button
							style={{ width: 215 }}
							type="primary"
							disabled={!selectedPOId}
							onClick={() => {
								form.setFieldValue(
									fieldName(['approvals', 0, 'approvalAction']),
									'approve',
								);
								setApprovalModal(true);
							}}
						>
							APPROVE PO
						</Button>
					</Row>
				</Row>
			</Space>
		</>
	);
};

export default FBDHPOApprovalListPage;
